import { useState } from 'react'
import { useAvailablePricings } from '../../../modules/apis/ai_tutor/queries'
import { CUSTOMER_TYPE_SLUGS } from '../../../modules/apis/sanity'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsInApp from '../../../modules/hooks/common/useIsInApp'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { s_pricing } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { COUNTRY_CURRENCY } from '../../../modules/vars'
import AITutorPricingCard from './AITutorPricingCard'
import BillingTypeSelector from './BillingTypeSelector'
import PricingFaqSection from './PricingFaqSection'
import { AITutorPricingSwitchCurrency } from './PricingSwitchCurrency'

export interface IPricingData {
  id: number
  billing_period_unit: string
  billing_period_number: number
  platform: string
  name: string
  original_prices: Prices
  monthly_original_prices: Prices
  sale_prices: Prices
  monthly_prices: Prices
  permissions: Permissions
  is_primary?: any
}

export interface Prices {
  KRW: string
  USD: string
  CNY: string
  JPY: string
  TWD: string
  VND: string
}

export interface Permissions {
  analysis: boolean
  roleplay: boolean
  curriculum: boolean
  discussion: boolean
}

export function extractNumber(price) {
  // 정규식을 사용해 숫자와 소수점만 남기고 제거
  if (!price) return

  return price.replace(/[^\d.]/g, '')
}

const percentage = (pricingData, currency) => {
  const isKRW = currency === 'KRW'
  const isUSD = currency === 'USD'
  const isOtherCurrency = !isKRW && !isUSD

  const originalPrice = extractNumber(pricingData?.original_prices[isOtherCurrency ? 'USD' : currency])
  const salePrice = extractNumber(pricingData?.sale_prices[isOtherCurrency ? 'USD' : currency])
  const discountPercentage = Math.floor(((originalPrice - salePrice) / originalPrice) * 100)
  return discountPercentage + '%'
}

// 여기는 구매 리스트 -> 구매 버튼 클릭
const PricingAITutorMembership = ({ isB2B = false }) => {
  const router = useAppRouter()
  const { currentUser } = useCurrentUser()
  const { t, isLocaleKO } = useTranslation()
  const getFullUrl = useReturnFullUrl()
  const isMobile = useIsMobile()
  const isInApp = useIsInApp()
  const isWebview = useIsWebview()

  const [billingType, setBillingType] = useState('YEAR')
  const [currency, setCurrency] = useState(COUNTRY_CURRENCY.KRW)

  const { data, isLoading } = useAvailablePricings(isB2B ? 'b2b2c' : 'web', billingType, null) // Ensure that platform has a value

  const handlePurchaseClick = (id) => {
    const detailUrl =
      urls.student.newPortal.pricing.aiTutor.detail(id) + `?country_code=${currency == 'KRW' ? 'KR' : 'US '}`

    if (currentUser) {
      if (isWebview && !isInApp) {
        router.push(detailUrl, {
          appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
          appSendData: {
            url: getFullUrl(detailUrl),
          },
        })
        return
      }

      router.push(detailUrl)
      return
    }

    router.push(`${urls.student.auth.new}?redirect_url=/${detailUrl}`)
  }

  if (isLoading) return null

  const pricingData: IPricingData[] = data?.pricings || []

  const isKRW = currency === 'KRW'
  const isUSD = currency === 'USD'
  const isOtherCurrency = !isKRW && !isUSD

  const isYearly = billingType === 'YEAR'
  const premiumDiscountPrice = isB2B
    ? isYearly
      ? isLocaleKO
        ? '20,000'
        : '15'
      : isLocaleKO
      ? '19,000'
      : '14.61'
    : isLocaleKO
    ? '150,000'
    : '115'

  return (
    <div className="sm:mx-auto px-[16px] lg:px-[0px]">
      <div className="mx-auto">
        <div className={`mb-32 flex sm:flex-row flex-col gap-y-16 ${isB2B ? 'justify-between' : 'sm:justify-end'}`}>
          {isB2B && (
            <BillingTypeSelector maxDiscountRate={'73%'} selectedType={billingType} setSelectedType={setBillingType} />
          )}
          <div className="max-w-[89px]">
            <AITutorPricingSwitchCurrency setCurrency={setCurrency} currency={currency} />
          </div>
        </div>

        {!isB2B && !isKRW && isMobile && (
          <ul className="pt-16 pb-20">
            <li className="Body2-14Md text-gray-500">*{t(s_pricing.notice_usd)}</li>
            <li className="Body2-14Md text-gray-500">*{t(s_pricing.notice_exchange)}</li>
          </ul>
        )}

        <div className="flex flex-col sm:flex-row gap-[16px] w-full mt-[16px]">
          {pricingData.map((item, index) => (
            <AITutorPricingCard
              key={index}
              data={item}
              discountRate={percentage(item, currency)}
              originalPrice={item?.original_prices[isOtherCurrency ? 'USD' : currency]}
              totalPriceLabel={item?.sale_prices[isOtherCurrency ? 'USD' : currency]}
              premiumDiscountPrice={index === 0 ? premiumDiscountPrice : undefined}
              premiumDiscountPriceLabel={''}
              discountPriceLabel={''}
              isPrimary={item.is_primary}
              isB2B={isB2B}
              currency={currency}
              onPurchase={handlePurchaseClick}
            />
          ))}
        </div>
        {!isB2B && !isKRW && !isMobile && (
          <ul className="mt-16">
            <li className="Body2-14Md text-gray-500">*{t(s_pricing.notice_usd)}</li>
            <li className="Body2-14Md text-gray-500">*{t(s_pricing.notice_exchange)}</li>
          </ul>
        )}
      </div>

      <div className="mt-[16px] flex flex-col gap-y-[16px]">
        {/* FAQ */}
        <div className="mb-[40px] sm:mb-[0px]">
          <PricingFaqSection slug={CUSTOMER_TYPE_SLUGS.B2C_AI_TUTOR} className="!px-[0px] sm:!p-[32px]" />
        </div>
      </div>
    </div>
  )
}

export default PricingAITutorMembership
